import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { businessHeader } from "@/lib/outlet";
import { memberBalance, pickDueMembership } from "@/lib/membership";
import {
    ACCOUNT_FILTERS,
    GENDER_FILTERS,
    SORT_OPTIONS,
    applyCustomerFilters,
    sortCustomerList,
    accountFilterCounts,
    hasActiveFilters,
} from "@/lib/customerFilters";
import { openReceiptPrint, downloadReceipt } from "@/lib/receipt";
import { printInvoiceById, downloadInvoiceById } from "@/lib/receiptActions";
import { canAccess } from "@/lib/permissions";
import MembershipCollectDialog from "@/components/MembershipCollectDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Phone, Mail, Calendar, IndianRupee, Users, Crown, AlertCircle, Pencil, Printer, Download, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        : "—";

function AccountStatus({ account }) {
    if (!account || account.status === "clear") {
        return <span className="text-xs font-semibold text-[#6B9E7A]">Clear</span>;
    }
    if (account.status === "balance_due" || account.status === "attention") {
        return <span className="text-xs font-bold tabular-nums text-[#C84B31]">{inr(account.balance_due)} due</span>;
    }
    if (account.status === "pending") {
        return <span className="text-xs font-bold text-[#C84B31]">Pending</span>;
    }
    return <span className="text-xs text-[#8A8178]">—</span>;
}

function ReceiptStatus({ invoice }) {
    const status = invoice.payment_status || "paid";
    if (status === "paid") {
        return <span className="rounded-full bg-[#E8F5EC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#2D7A4F]">Paid</span>;
    }
    if (status === "pending") {
        return <span className="rounded-full bg-[#FDE8EC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#C84B31]">Pending</span>;
    }
    return <span className="rounded-full bg-[#FAF9F6] px-2 py-0.5 text-[10px] font-bold uppercase text-[#8A8178]">{status}</span>;
}

function MembershipPayStatus({ membership: m }) {
    const bal = memberBalance(m);
    if (bal > 0) return <span className="text-xs font-bold tabular-nums text-[#C84B31]">{inr(bal)} due</span>;
    if (m.payment_status === "pending") return <span className="text-xs font-bold text-[#C84B31]">Pending</span>;
    return <span className="text-xs font-semibold text-[#6B9E7A]">Paid</span>;
}

const filterChip =
    "h-8 shrink-0 rounded-full border px-3 text-xs font-bold transition-colors";

const filterChipActive =
    "border-[#4A0E17] bg-[#4A0E17] text-white shadow-sm";

const filterChipIdle =
    "border-[#E8E4DE] bg-white text-[#5C534C] hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const iconBtn =
    "size-8 text-[#8A8178] hover:bg-[#FFFEF9] hover:text-[#4A0E17]";

const EDIT_ROLES = new Set(["owner", "manager", "receptionist"]);

function canEditCustomers(user) {
    return EDIT_ROLES.has(user?.role);
}

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

export default function Customers() {
    const { user } = useAuth();
    const canEdit = canEditCustomers(user);
    const canCollect = canAccess(user?.role, "memberships");
    const [searchParams] = useSearchParams();
    const [list, setList] = useState([]);
    const [q, setQ] = useState(() => searchParams.get("q") || "");
    const [open, setOpen] = useState(false);
    const [editCustomer, setEditCustomer] = useState(null);
    const [active, setActive] = useState(null);
    const [activeData, setActiveData] = useState(null);
    const [outlets, setOutlets] = useState([]);
    const [collectMember, setCollectMember] = useState(null);
    const [accountFilter, setAccountFilter] = useState(() => searchParams.get("filter") || "all");
    const [genderFilter, setGenderFilter] = useState(() => searchParams.get("gender") || "all");
    const [sortBy, setSortBy] = useState(() => searchParams.get("sort") || "newest");
    const profileIdRef = useRef(null);

    const filterCounts = useMemo(() => accountFilterCounts(list), [list]);

    const displayedList = useMemo(() => {
        const filtered = applyCustomerFilters(list, {
            accountFilter,
            gender: genderFilter,
        });
        return sortCustomerList(filtered, sortBy);
    }, [list, accountFilter, genderFilter, sortBy]);

    const filtersActive = hasActiveFilters({ accountFilter, gender: genderFilter });

    const resetFilters = () => {
        setAccountFilter("all");
        setGenderFilter("all");
        setSortBy("newest");
    };

    const load = async () => {
        const r = await api.get("/customers", { params: { q } });
        setList(r.data);
    };

    useEffect(() => {
        const fromUrl = searchParams.get("q");
        if (fromUrl != null && fromUrl !== q) setQ(fromUrl);
        if (searchParams.get("new") === "1" && canEdit) setOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, canEdit]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    useEffect(() => {
        if (!active) {
            setOutlets([]);
            return undefined;
        }
        let cancelled = false;
        api.get("/outlets")
            .then((r) => { if (!cancelled) setOutlets(r.data || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [active?.id]);

    const refreshProfile = async () => {
        const id = profileIdRef.current;
        if (!id) return;
        await load();
        try {
            const r = await api.get(`/customers/${id}`);
            if (profileIdRef.current === id) {
                setActiveData(r.data);
                setActive((prev) => (prev?.id === id ? { ...prev, ...(r.data.customer || {}) } : prev));
            }
        } catch {
            /* list refresh is enough */
        }
    };

    const enrichMemberForCollect = (m, customer) => ({
        ...m,
        customer: { full_name: customer.full_name, mobile: customer.mobile },
    });

    const openCollect = (member) => {
        const c = activeData?.customer ?? active;
        if (!member || !c) return;
        setCollectMember(enrichMemberForCollect(member, c));
    };

    const openCollectPrimary = () => {
        const m = pickDueMembership(activeData?.memberships || []);
        if (m) openCollect(m);
    };

    const invoiceForReceipt = (iv) => {
        const c = activeData?.customer ?? active;
        return {
            ...iv,
            customer: iv.customer || { full_name: c?.full_name, mobile: c?.mobile },
        };
    };

    const receiptBusiness = (iv) => businessHeader(outlets, user, iv?.outlet_id);

    const handleReceiptPrint = async (iv, e) => {
        e?.stopPropagation?.();
        const c = activeData?.customer ?? active;
        const ctx = {
            outlets,
            user,
            customerFallback: c ? { full_name: c.full_name, mobile: c.mobile } : undefined,
        };
        try {
            const ok = iv.id
                ? await printInvoiceById(iv.id, ctx)
                : openReceiptPrint(invoiceForReceipt(iv), receiptBusiness(iv));
            if (!ok) toast.error("Allow pop-ups to print the receipt");
        } catch {
            toast.error("Could not load receipt");
        }
    };

    const handleReceiptDownload = async (iv, e) => {
        e?.stopPropagation?.();
        const c = activeData?.customer ?? active;
        const ctx = {
            outlets,
            user,
            customerFallback: c ? { full_name: c.full_name, mobile: c.mobile } : undefined,
        };
        try {
            if (iv.id) {
                await downloadInvoiceById(iv.id, ctx);
            } else {
                downloadReceipt(invoiceForReceipt(iv), receiptBusiness(iv));
            }
            toast.success("Receipt downloaded");
        } catch {
            toast.error("Could not download receipt");
        }
    };

    const openProfile = async (c) => {
        profileIdRef.current = c.id;
        setActive(c);
        setActiveData(null);
        try {
            const r = await api.get(`/customers/${c.id}`);
            if (profileIdRef.current === c.id) {
                setActiveData(r.data);
            }
        } catch {
            toast.error("Could not load customer profile");
            if (profileIdRef.current === c.id) {
                closeProfile();
            }
        }
    };

    const closeProfile = () => {
        profileIdRef.current = null;
        setActive(null);
        setActiveData(null);
    };

    const openEdit = (c, e) => {
        e?.stopPropagation?.();
        setEditCustomer(c);
    };

    const startEditFromProfile = () => {
        const c = activeData?.customer ?? active;
        if (!c) return;
        closeProfile();
        setEditCustomer(c);
    };

    const onCustomerSaved = async (updated) => {
        await load();
        if (updated?.id && active?.id === updated.id) {
            setActive((prev) => (prev ? { ...prev, ...updated } : prev));
            if (profileIdRef.current === updated.id) {
                try {
                    const r = await api.get(`/customers/${updated.id}`);
                    if (profileIdRef.current === updated.id) setActiveData(r.data);
                } catch { /* list refresh is enough */ }
            }
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="customers-page">
            <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className={EYEBROW} style={{ color: GOLD }}>
                        Clients
                    </p>
                    <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                        Customers
                    </h1>
                    <GoldDivider className="mt-3 max-w-[200px]" />
                    <p className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#8A8178]">
                        <Users className="size-4 text-[#D4AF37]" />
                        {filtersActive || q
                            ? `${displayedList.length} of ${list.length} client${list.length !== 1 ? "s" : ""}`
                            : `${list.length} client${list.length !== 1 ? "s" : ""}`}
                        {filterCounts.due > 0 && (
                            <>
                                <span className="text-[#E8E4DE]">|</span>
                                <span className="text-[#C84B31]">{filterCounts.due} with balance due</span>
                            </>
                        )}
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]"
                            data-testid="add-customer-button"
                        >
                            <Plus className="mr-1 size-4" /> Add customer
                        </Button>
                    </DialogTrigger>
                    <CustomerDialog
                        onClose={() => setOpen(false)}
                        onSaved={async (data) => {
                            await onCustomerSaved(data);
                            setOpen(false);
                        }}
                    />
                </Dialog>
                {canEdit && (
                    <Dialog open={!!editCustomer} onOpenChange={(o) => !o && setEditCustomer(null)}>
                        {editCustomer && (
                            <CustomerDialog
                                customer={editCustomer}
                                onClose={() => setEditCustomer(null)}
                                onSaved={async (data) => {
                                    await onCustomerSaved(data);
                                    setEditCustomer(null);
                                }}
                            />
                        )}
                    </Dialog>
                )}
            </header>

            <div className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative max-w-md flex-1">
                        <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name or mobile…"
                            className="h-10 rounded-full border-[#E8E4DE] bg-[#FFFEF9] pl-10 text-sm font-medium text-[#3D2817] shadow-sm placeholder:text-[#9A9088] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20"
                            data-testid="customer-search"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={genderFilter} onValueChange={setGenderFilter}>
                            <SelectTrigger
                                className="h-10 w-[140px] rounded-full border-[#E8E4DE] bg-[#FFFEF9] text-xs font-semibold"
                                data-testid="customer-gender-filter"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {GENDER_FILTERS.map((g) => (
                                    <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger
                                className="h-10 w-[150px] rounded-full border-[#E8E4DE] bg-[#FFFEF9] text-xs font-semibold"
                                data-testid="customer-sort"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_OPTIONS.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {filtersActive && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`h-10 rounded-full ${navBtn}`}
                                onClick={resetFilters}
                                data-testid="customer-reset-filters"
                            >
                                Reset
                            </Button>
                        )}
                    </div>
                </div>

                <div className="scrollbar-salon flex gap-2 overflow-x-auto pb-1" data-testid="customer-filter-chips">
                    {ACCOUNT_FILTERS.map((f) => {
                        const count = filterCounts[f.id] ?? 0;
                        const active = accountFilter === f.id;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => setAccountFilter(f.id)}
                                className={`${filterChip} ${active ? filterChipActive : filterChipIdle}`}
                                data-testid={`customer-filter-${f.id}`}
                            >
                                {f.label}
                                <span className={`ml-1.5 tabular-nums ${active ? "text-white/85" : "text-[#9A9088]"}`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />

                <div className="scrollbar-salon overflow-x-auto pb-1">
                    <table className="w-full min-w-[760px] text-sm">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] bg-gradient-to-r from-[#FFFEF9] to-white text-left text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                                <th className="w-12 px-3 py-3.5 text-center">Sr.no</th>
                                <th className="px-5 py-3.5">Name</th>
                                <th className="px-5 py-3.5">Mobile</th>
                                <th className="px-5 py-3.5">Email</th>
                                <th className="px-5 py-3.5 text-right">Last visit</th>
                                <th className="px-5 py-3.5 text-right">Account</th>
                                {canEdit && <th className="w-14 px-3 py-3.5 text-center"> </th>}
                            </tr>
                        </thead>
                        <tbody>
                            {list.length === 0 && (
                                <tr>
                                    <td colSpan={canEdit ? 7 : 6} className="px-5 py-14 text-center">
                                        <p className="font-serif text-lg font-semibold text-[#4A0E17]">No customers yet</p>
                                        <p className="mt-1 text-sm font-medium text-[#8A8178]">Add your first client to get started.</p>
                                    </td>
                                </tr>
                            )}
                            {list.length > 0 && displayedList.length === 0 && (
                                <tr>
                                    <td colSpan={canEdit ? 7 : 6} className="px-5 py-14 text-center">
                                        <p className="font-serif text-lg font-semibold text-[#4A0E17]">No matches</p>
                                        <p className="mt-1 text-sm font-medium text-[#8A8178]">Try another filter or clear search.</p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className={`mt-3 ${navBtn}`}
                                            onClick={() => { resetFilters(); setQ(""); }}
                                        >
                                            Clear filters
                                        </Button>
                                    </td>
                                </tr>
                            )}
                            {displayedList.map((c, idx) => (
                                <tr
                                    key={c.id}
                                    onClick={() => openProfile(c)}
                                    className="cursor-pointer border-t border-[#E8E4DE]/80 transition-colors hover:bg-[#FFFEF9]"
                                    data-testid={`customer-row-${c.id}`}
                                >
                                    <td className="px-3 py-3.5 text-center text-xs font-bold tabular-nums text-[#9A9088]">
                                        {idx + 1}
                                    </td>
                                    <td className="px-5 py-3.5 font-bold text-[#3D2817]">{c.full_name}</td>
                                    <td className="px-5 py-3.5 font-medium text-[#8A8178]">{c.mobile}</td>
                                    <td className="px-5 py-3.5 font-medium text-[#8A8178]">{c.email || "—"}</td>
                                    <td className="px-5 py-3.5 text-right font-semibold tabular-nums text-[#3D2817]">
                                        {fmtDate(c.last_visit_at)}
                                    </td>
                                    <td className="px-5 py-3.5 text-right">
                                        <AccountStatus account={c.account} />
                                    </td>
                                    {canEdit && (
                                        <td className="px-3 py-3.5 text-center">
                                            <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                className={iconBtn}
                                                onClick={(e) => openEdit(c, e)}
                                                aria-label={`Edit ${c.full_name}`}
                                                data-testid={`edit-customer-${c.id}`}
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!active} onOpenChange={(v) => !v && closeProfile()}>
                <DialogContent
                    className="flex max-h-[min(90vh,720px)] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-xl border-[#E8E4DE] p-0 top-[4vh] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]"
                    data-testid="customer-profile-dialog"
                >
                    <DialogHeader className="shrink-0 space-y-0 border-b border-[#E8E4DE]/80 px-5 py-4 pr-12">
                        <div className="flex items-start justify-between gap-3">
                            <DialogTitle className="font-serif text-xl font-bold leading-tight" style={{ color: MAROON }}>
                                {(activeData?.customer ?? active)?.full_name}
                            </DialogTitle>
                            {canEdit && active && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className={`h-8 shrink-0 ${navBtn}`}
                                    onClick={startEditFromProfile}
                                    data-testid="edit-customer-profile"
                                >
                                    <Pencil className="mr-1.5 size-3.5" /> Edit
                                </Button>
                            )}
                        </div>
                        {active && (() => {
                            const c = activeData?.customer ?? active;
                            return (
                                <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-[#8A8178]">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Phone className="size-3.5 text-[#D4AF37]" /> {c.mobile || "—"}
                                    </span>
                                    {c.email && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Mail className="size-3.5 text-[#D4AF37]" /> {c.email}
                                        </span>
                                    )}
                                </p>
                            );
                        })()}
                    </DialogHeader>

                    <div className="scrollbar-dialog min-h-0 flex-1 overflow-y-auto overscroll-contain py-4 pl-5 pr-3">
                    {!activeData && active && (
                        <p className="py-8 text-center text-sm font-medium text-[#8A8178]">Loading…</p>
                    )}
                    {activeData && active && (() => {
                        const c = activeData.customer ?? active;
                        const account = activeData.account;
                        const activeMemberships = (activeData.memberships || []).filter(
                            (m) => m.expires_at && m.expires_at >= new Date().toISOString(),
                        );
                        return (
                        <div className="space-y-4">
                            {account && account.status !== "clear" && (
                                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#F0D4D4] bg-[#FFF5F5] px-3 py-2.5 text-sm">
                                    <span className="inline-flex items-center gap-2 font-semibold text-[#7A2438]">
                                        <AlertCircle className="size-4 shrink-0 text-[#C84B31]" />
                                        {account.balance_due > 0 && <span>{inr(account.balance_due)} due</span>}
                                        {account.balance_due > 0 && account.pending_online_count > 0 && <span className="text-[#E8E4DE]">·</span>}
                                        {account.pending_online_count > 0 && (
                                            <span>{account.pending_online_count} pending online</span>
                                        )}
                                    </span>
                                    {account.balance_due > 0 && canCollect && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className={`h-8 text-xs ${navBtn}`}
                                            onClick={openCollectPrimary}
                                            data-testid="customer-collect-due"
                                        >
                                            <Wallet className="mr-1 size-3.5" />
                                            Collect
                                        </Button>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-2">
                                <ProfileStat label="Last visit" value={fmtDate(c.last_visit_at)} tone="bookings" />
                                <ProfileStat
                                    label="Account"
                                    value={
                                        account?.status === "clear"
                                            ? "Clear"
                                            : account?.balance_due > 0
                                              ? inr(account.balance_due)
                                              : "Pending"
                                    }
                                    tone={account?.status === "clear" ? "customers" : "revenue"}
                                />
                                <ProfileStat
                                    label="Member"
                                    value={
                                        activeMemberships.length > 0
                                            ? activeMemberships[0].plan?.name || "Yes"
                                            : "—"
                                    }
                                    tone="customers"
                                />
                            </div>

                            {c.notes && (
                                <p className="rounded-lg border border-[#E8E4DE] bg-[#FFFEF9]/60 px-3 py-2 text-sm text-[#3D2817]">
                                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#8A8178]">Notes · </span>
                                    {c.notes}
                                </p>
                            )}

                            {(activeData.memberships || []).length > 0 && (
                                <ProfileSection title="Memberships" icon={Crown}>
                                    {activeData.memberships.map((m) => (
                                        <ProfileRow
                                            key={m.id}
                                            left={`${m.plan?.name || "Plan"} · ${fmtDate(m.expires_at)}`}
                                            right={(
                                                <span className="flex items-center gap-2">
                                                    <MembershipPayStatus membership={m} />
                                                    {canCollect && memberBalance(m) > 0 && (
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className={`h-7 px-2 text-[10px] ${navBtn}`}
                                                            onClick={(e) => { e.stopPropagation(); openCollect(m); }}
                                                        >
                                                            Collect
                                                        </Button>
                                                    )}
                                                </span>
                                            )}
                                        />
                                    ))}
                                </ProfileSection>
                            )}

                            <ProfileSection title="Appointments" icon={Calendar}>
                                {activeData.appointments.length === 0 ? (
                                    <p className="py-1 text-sm text-[#8A8178]">None</p>
                                ) : (
                                    activeData.appointments.map((a) => (
                                        <ProfileRow
                                            key={a.id}
                                            left={new Date(a.start_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                            right={<span className="text-xs font-bold capitalize text-[#8A8178]">{a.status}</span>}
                                        />
                                    ))
                                )}
                            </ProfileSection>

                            <ProfileSection title="Receipts" icon={IndianRupee}>
                                {activeData.invoices.length === 0 ? (
                                    <p className="py-1 text-sm text-[#8A8178]">None</p>
                                ) : (
                                    activeData.invoices.map((iv) => (
                                        <ProfileRow
                                            key={iv.id}
                                            left={iv.invoice_number}
                                            right={(
                                                <span className="flex items-center gap-1">
                                                    <ReceiptStatus invoice={iv} />
                                                    <span className="min-w-[3.5rem] font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(iv.grand_total)}</span>
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="ghost"
                                                        className={iconBtn}
                                                        title="Print receipt"
                                                        onClick={(e) => handleReceiptPrint(iv, e)}
                                                        data-testid={`print-receipt-${iv.id}`}
                                                    >
                                                        <Printer className="size-3.5" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="ghost"
                                                        className={iconBtn}
                                                        title="Download receipt"
                                                        onClick={(e) => handleReceiptDownload(iv, e)}
                                                        data-testid={`download-receipt-${iv.id}`}
                                                    >
                                                        <Download className="size-3.5" />
                                                    </Button>
                                                </span>
                                            )}
                                        />
                                    ))
                                )}
                            </ProfileSection>
                        </div>
                        );
                    })()}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!collectMember} onOpenChange={(o) => !o && setCollectMember(null)}>
                {collectMember && (
                    <MembershipCollectDialog
                        member={collectMember}
                        onClose={() => setCollectMember(null)}
                        onSaved={async () => {
                            setCollectMember(null);
                            await refreshProfile();
                        }}
                    />
                )}
            </Dialog>
        </div>
    );
}

const PROFILE_STAT = {
    revenue: { bg: "#FDF3DC", color: GOLD },
    bookings: { bg: "#E0EDFA", color: "#3B82C4" },
    customers: { bg: "#FBE4EF", color: "#D9468F" },
};

function ProfileStat({ label, value, tone = "revenue" }) {
    const { bg } = PROFILE_STAT[tone] ?? PROFILE_STAT.revenue;
    return (
        <div
            className="relative overflow-hidden rounded-lg border border-[#E8E4DE]/80 px-3 py-2.5"
            style={{ background: `linear-gradient(150deg, ${bg}44 0%, #FFFFFF 55%)` }}
        >
            <p className="text-[9px] font-extrabold uppercase tracking-[0.12em]" style={{ color: CHOCOLATE }}>{label}</p>
            <p className="mt-1 truncate font-serif text-base font-extrabold" style={{ color: CHOCOLATE }} title={String(value)}>
                {value}
            </p>
        </div>
    );
}

function ProfileSection({ title, icon: Icon, children }) {
    return (
        <div className="rounded-lg border border-[#E8E4DE] bg-[#FFFEF9]/40 px-3 py-2.5">
            <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#8A8178]">
                <Icon className="size-3.5 text-[#D4AF37]" />
                {title}
            </h3>
            <div className="space-y-0">{children}</div>
        </div>
    );
}

function ProfileRow({ left, right }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-[#E8E4DE]/70 py-2 text-sm last:border-0">
            <span className="min-w-0 truncate font-medium text-[#3D2817]">{left}</span>
            <span className="shrink-0">{right}</span>
        </div>
    );
}

function CustomerDialog({ customer, onClose, onSaved }) {
    const isEdit = !!customer;
    const [f, setF] = useState({
        full_name: "", mobile: "", email: "", gender: "female", notes: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (customer) {
            setF({
                full_name: customer.full_name || "",
                mobile: customer.mobile || "",
                email: customer.email || "",
                gender: customer.gender || "female",
                notes: customer.notes || "",
            });
        } else {
            setF({ full_name: "", mobile: "", email: "", gender: "female", notes: "" });
        }
    }, [customer]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f };
            if (!payload.email) payload.email = null;
            if (!payload.notes) payload.notes = "";
            let data;
            if (isEdit) {
                const r = await api.put(`/customers/${customer.id}`, payload);
                data = r.data;
                toast.success("Customer updated");
            } else {
                const r = await api.post("/customers", payload);
                data = r.data;
                toast.success("Customer added");
            }
            onSaved(data);
        } catch (err) {
            const msg = err?.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="scrollbar-dialog max-h-[min(90vh,640px)] overflow-y-auto overscroll-contain rounded-xl border-[#E8E4DE] p-6 pr-5 sm:max-w-md">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold text-[#4A0E17]">
                    {isEdit ? "Edit customer" : "Add customer"}
                </DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Name</Label>
                    <Input className={fieldClass} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required data-testid="cust-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Mobile</Label>
                        <Input className={fieldClass} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} required data-testid="cust-mobile" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Gender</Label>
                        <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
                            <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Email</Label>
                    <Input className={fieldClass} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" data-testid="cust-email" />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Notes</Label>
                    <Input className={fieldClass} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="cust-notes" />
                </div>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button type="submit" className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]" disabled={busy} data-testid="save-customer-button">
                        {busy ? "Saving…" : isEdit ? "Save changes" : "Save"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
